import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect
} from 'react';
import { API_BASE_URL } from '../utils/api';
import { logWarn } from '../utils/log';

const WorkflowsContext = createContext(null);

function groupWorkflows(list) {
  const by = {};
  for (const w of list || []) {
    if (!by[w.categoryId]) by[w.categoryId] = [];
    by[w.categoryId].push({ id: w.id, name: w.name });
  }
  return by;
}

export function WorkflowsProvider({ children, getToken, teamId }) {
  const [workflowsByCategory, setWorkflowsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const fetchWorkflows = useCallback(async () => {
    const token = await getToken();
    if (!token) {
      setWorkflowsByCategory({});
      throw new Error('Not signed in');
    }
    const teamParam = teamId ? `?teamId=${encodeURIComponent(teamId)}` : '';
    const res = await fetch(`${API_BASE_URL}/api/workflows${teamParam}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.message || 'Failed to load workflows');
      logWarn('workflows', 'GET /api/workflows', res.status, err.message);
      throw err;
    }
    setWorkflowsByCategory(groupWorkflows(data.workflows));
  }, [getToken, teamId]);

  const refreshWorkflows = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      await fetchWorkflows();
      setLoadError('');
    } catch (e) {
      logWarn('workflows', 'refreshWorkflows:', e?.message || e);
      setLoadError(e.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [fetchWorkflows]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchWorkflows()
      .then(() => {
        if (!cancelled) setLoadError('');
      })
      .catch((e) => {
        if (!cancelled) {
          logWarn('workflows', 'initial load:', e?.message || e);
          setLoadError(e.message || 'Failed to load workflows');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchWorkflows]);

  const addWorkflow = useCallback(
    async (categoryId, name) => {
      const token = await getToken();
      if (!token) throw new Error('Not signed in');
      const res = await fetch(`${API_BASE_URL}/api/workflows`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ categoryId, name: name.trim(), teamId: teamId || '' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data.message || 'Failed to create workflow');
        logWarn('workflows', 'POST /api/workflows', res.status, err.message);
        throw err;
      }
      setWorkflowsByCategory((prev) => ({
        ...prev,
        [categoryId]: [...(prev[categoryId] || []), { id: data.id, name: data.name }]
      }));
      return data.id;
    },
    [getToken, teamId]
  );

  const value = useMemo(
    () => ({
      workflowsByCategory,
      loading,
      loadError,
      addWorkflow,
      refreshWorkflows
    }),
    [workflowsByCategory, loading, loadError, addWorkflow, refreshWorkflows]
  );

  return <WorkflowsContext.Provider value={value}>{children}</WorkflowsContext.Provider>;
}

export function useWorkflows() {
  const ctx = useContext(WorkflowsContext);
  if (!ctx) {
    throw new Error('useWorkflows must be used within WorkflowsProvider');
  }
  return ctx;
}
